import React, { useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { Button } from 'components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from 'components/ui/dialog';
import { RadioGroup, RadioGroupItem } from 'components/ui/radio-group';
import { Textarea } from 'components/ui/textarea';
import { Plus, Star, X } from 'lucide-react';
import { useUploadReview, useUploadReviewImages } from 'shared/api';
import { ReviewServiceType } from 'shared/types';
import { toast } from 'sonner';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  orderId: number;
  reviewTargetServiceType: ReviewServiceType;
};

export function ReviewWriteDialog({ isOpen, onClose, onSubmit, orderId, reviewTargetServiceType }: Props) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [satisfaction, setSatisfaction] = useState<number | null>(null);
  const [images, setImages] = useState<File[]>([]);

  const queryClient = useQueryClient();

  const { mutateAsync: uploadReviewImages, isPending: isUploadReviewImagesPending } = useUploadReviewImages(images);

  const { mutateAsync: uploadReview, isPending: isUploadReviewPending } = useUploadReview({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['memberNotification'] });

      onClose();
      onSubmit();
    },
    onError: (message) => {
      toast.error(message);
    },
  });

  const isSubmitEnabled = rating > 0 && comment.length >= 80 && satisfaction !== null;

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (images.length + files.length <= 3) {
      setImages((prev) => [...prev, ...files]);
    }
  };

  const handleRemoveImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!isSubmitEnabled) return;

    const uploadedImagesUrl = await uploadReviewImages();

    uploadReview({
      rating,
      contents_raw: comment,
      satisfaction,
      reviewImageUrls: uploadedImagesUrl,
      orderId,
      isExtra: true,
      serviceType: [reviewTargetServiceType],
    });
  };

  const handleClose = () => {
    setRating(0);
    setComment('');
    setSatisfaction(null);
    setImages([]);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="!w-[calc(100vw-48px)] !max-w-[calc(100vw-48px)] rounded-[32px] border-0 bg-white p-5 shadow-[0_4px_22px_rgba(0,0,0,0.12)] md:!w-[514px] md:!max-w-[514px] md:p-[36px_42px]">
        <DialogTitle className="sr-only">Write a review</DialogTitle>
        <DialogDescription className="sr-only">Review form dialog</DialogDescription>

        <div className="flex w-full flex-col items-center md:w-[436px]">
          <div className="flex w-full flex-col gap-[10px]">
            <div className="flex flex-col gap-[8px]">
              <h2 className="text-[16px] font-semibold leading-[1.5] tracking-[-0.022em] text-[#222222]">
                Write a review
              </h2>
              <div className="h-px w-full bg-[#EAEAEA]" />
            </div>
          </div>

          <div className="mb-6 mt-5 flex w-full flex-col gap-5 md:mb-[46px] md:mt-[30px] md:gap-[30px]">
            <div className="flex w-full flex-col gap-[6px]">
              <div className="flex flex-col gap-[8px]">
                <h3 className="text-[13.5px] font-semibold leading-[1.5] tracking-[-0.022em] text-[#222222] md:text-[15px]">
                  How did it feel using CheckmateKorea?
                </h3>
                <div className="flex items-center gap-[1px]">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button key={star} type="button" onClick={() => setRating(star)} className="h-[25px] w-[25px]">
                      <Star
                        className={`h-full w-full ${
                          star <= rating ? 'fill-[#FFD700] text-[#FFD700]' : 'text-[#DFDFDF]'
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex w-full flex-col gap-[10px]">
              <div className="flex flex-col gap-[8px]">
                <h3 className="text-[13.5px] font-semibold leading-[1.5] tracking-[-0.022em] text-[#222222] md:text-[15px]">
                  How was your experience?
                </h3>

                <div className="flex flex-wrap gap-[10px]">
                  {images.length < 3 && (
                    <div className="flex h-[80px] w-[80px] items-center justify-center rounded-[8px] bg-[#F6F6F6] md:h-[100px] md:w-[100px]">
                      <label className="flex cursor-pointer flex-col items-center gap-[1px]">
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleImageUpload}
                          className="hidden"
                          disabled={images.length >= 3}
                        />
                        <div className="flex h-[16px] w-[16px] items-center justify-center">
                          <Plus className="text-bold h-[12px] w-[12px] text-[#3F3F3F]" />
                        </div>
                        <span className="text-center text-[12px] font-medium leading-[1.4] tracking-[-0.022em] text-[#3F3F3F]">
                          Image
                          <br />
                          (optional, up to 3)
                        </span>
                      </label>
                    </div>
                  )}

                  {images.map((image, index) => (
                    <div key={index} className="relative h-[80px] w-[80px] md:h-[100px] md:w-[100px]">
                      <img
                        src={URL.createObjectURL(image)}
                        alt={`Upload ${index + 1}`}
                        className="h-full w-full rounded-[8px] object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(index)}
                        className="absolute right-[5px] top-[5px] flex h-[20px] w-[20px] items-center justify-center rounded-full bg-white shadow-md"
                      >
                        <X className="h-[12px] w-[12px] text-[#3F3F3F]" />
                      </button>
                    </div>
                  ))}
                </div>

                <div className="flex flex-col gap-[4px]">
                  <Textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder={`Add a comment... Any language is fine.\n(Minimum 80 characters)`}
                    className="min-h-[160px] resize-none rounded-[4px] border border-[#DFDFDF] bg-white p-[14px_16px] text-[14px] font-normal leading-[1.5] tracking-[-0.022em] text-[#222222] placeholder:text-[#8E8E8E] focus:border-[#222222] focus:outline-none md:min-h-[240px]"
                    maxLength={800}
                  />
                  <div className="text-right text-[12px] font-medium leading-[1.5] tracking-[-0.022em] text-[#BEBEBE]">
                    {comment.length}/800
                  </div>
                </div>
              </div>
            </div>

            <div className="flex w-full flex-col gap-[14px]">
              <div className="flex flex-col gap-[4px]">
                <h3 className="text-[13.5px] font-semibold leading-[1.5] tracking-[-0.022em] text-[#222222] md:text-[15px]">
                  Would you recommend our service to a friend?
                </h3>
                <p className="text-[14px] font-medium leading-[1.5] tracking-[-0.022em] text-[#8E8E8E]">
                  0 = Not at all likely, 10 = Extremely likely
                </p>
              </div>

              <div className="flex flex-col gap-[18px]">
                <RadioGroup
                  value={satisfaction?.toString() || ''}
                  onValueChange={(value) => setSatisfaction(Number(value))}
                  className="flex flex-wrap items-center justify-between gap-2 md:justify-start md:gap-[18px]"
                >
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((value) => (
                    <div key={value} className="flex flex-col items-center gap-[2px]">
                      <span className="text-[14px] font-medium leading-[1.5] tracking-[-0.022em] text-[#3F3F3F]">
                        {value}
                      </span>
                      <RadioGroupItem
                        value={value.toString()}
                        className="h-[18px] w-[18px] border-2 border-[#B8BCC1]"
                      />
                    </div>
                  ))}
                </RadioGroup>
              </div>
            </div>
          </div>

          <div className="flex w-full flex-col gap-[13px]">
            <Button
              onClick={handleSubmit}
              disabled={!isSubmitEnabled || isUploadReviewImagesPending || isUploadReviewPending}
              className={`h-[40px] w-full rounded-[8px] px-[10px] py-[10px] text-[13px] font-semibold leading-[1.5] tracking-[-0.022em] ${
                isSubmitEnabled
                  ? 'bg-primary text-white hover:bg-primary/90'
                  : 'cursor-not-allowed bg-[#BEBEBE] text-white'
              }`}
            >
              Submit
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
